import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FaArrowLeft, FaMinus, FaPlus, FaShoppingCart } from 'react-icons/fa';

import productosService from '../services/productosService';
import analyticsService, { COMMERCE_SOURCES } from '../services/analyticsService';
import useProductActions from '../hooks/useProductActions';
import { useCart } from '../contexts/CartContext';
import ProductGallery from '../components/products/ProductGallery';
import FichaTecnica from '../components/products/FichaTecnica';
import { useSeo } from '../components/common/Seo';

const formatearPrecio = valor =>
  typeof valor === 'number' && Number.isFinite(valor) ? `$${valor.toLocaleString()}` : null;

export default function ProductDetailPage() {
  const { id } = useParams();
  const location = useLocation();

  const [producto, setProducto] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [cantidad, setCantidad] = useState(1);

  const { addToCartTracked, buyNow } = useProductActions(COMMERCE_SOURCES.FICHA_PRODUCTO);
  const { getItemQuantity } = useCart();

  // Guarda contra el doble disparo de los efectos en React StrictMode, que en
  // desarrollo montaría la página dos veces y contaría dos visitas por una.
  const vistaRegistrada = useRef(null);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    setError(null);
    setCantidad(1);

    productosService
      .getProducto(id)
      .then(data => {
        if (cancelado) return;
        setProducto(data);
      })
      .catch(err => {
        if (cancelado) return;
        setError(err.message || 'No pudimos cargar el producto');
        setProducto(null);
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [id]);

  useEffect(() => {
    if (!producto || vistaRegistrada.current === producto.id) return;
    vistaRegistrada.current = producto.id;
    // El origen viaja en el state del enlace: así se sabe si la visita vino
    // del catálogo, de la home o de un enlace directo.
    analyticsService.trackProductPageView(producto, location.state?.source || null);
  }, [producto, location.state]);

  // Título y descripción de la pestaña al navegar dentro de la SPA. En la
  // primera carga ya vienen resueltos del servidor (seo_render_controller.go);
  // esto cubre el caso de llegar aquí desde el catálogo, donde no hay petición
  // de página y el <title> se quedaría con el de la página anterior.
  useSeo({
    titulo: producto ? `${producto.Juego} — Juego de mesa | Tablero Infinito` : null,
    descripcion: producto?.descripcion?.slice(0, 155) || null,
    // Una ficha que no carga no debe indexarse: el backend ya responde 404,
    // pero si se llega por navegación interna no hay respuesta que marcar.
    robots: error ? 'noindex, follow' : undefined,
  });

  const stock = Number(producto?.cantidad ?? 0);
  const agotado = stock <= 0;
  const enCarrito = producto ? getItemQuantity(producto) : 0;

  const cambiarCantidad = useCallback(
    delta => {
      setCantidad(actual => {
        const siguiente = actual + delta;
        if (siguiente < 1) return 1;
        if (stock > 0 && siguiente > stock) return stock;
        return siguiente;
      });
    },
    [stock]
  );

  if (cargando) {
    return (
      <div className="container mx-auto px-4 py-10 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 animate-pulse">
          <div className="df-card aspect-square" />
          <div className="space-y-4">
            <div className="h-8 bg-df-surface2 rounded w-3/4" />
            <div className="h-4 bg-df-surface2 rounded w-1/3" />
            <div className="h-10 bg-df-surface2 rounded w-1/2" />
            <div className="h-24 bg-df-surface2 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !producto) {
    return (
      <div className="container mx-auto px-4 py-20 max-w-2xl text-center">
        <h1 className="font-cinzel text-3xl text-df-text mb-3">Producto no encontrado</h1>
        <p className="text-df-muted mb-8">
          {error === 'Producto no encontrado' || !error
            ? 'Puede que ya no esté disponible o que el enlace esté equivocado.'
            : error}
        </p>
        <Link to="/productos" className="btn btn-primary">
          Ver todos los juegos
        </Link>
      </div>
    );
  }

  const precioPublico = producto.Precio_Publico;
  const precioOferta = producto.Precio_Oferta;
  const enOferta = producto.Oferta?.toLowerCase() === 'si' && precioOferta != null;
  const precioFinal = enOferta ? precioOferta : precioPublico;

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <Link
        to="/productos"
        className="inline-flex items-center gap-2 text-sm text-df-muted hover:text-df-accent transition-colors mb-6"
      >
        <FaArrowLeft className="text-xs" /> Volver al catálogo
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
        <ProductGallery
          imagenes={producto.imagenes?.length ? producto.imagenes : [producto.Imagen]}
          alt={producto.Juego}
          agotado={agotado}
        />

        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            {producto.EsNuevo?.toLowerCase() === 'si' && (
              <span className="text-xs font-bold px-2 py-1 rounded bg-df-accent/20 text-df-accent">
                ✨ NUEVO
              </span>
            )}
            {producto.Preventa?.toLowerCase() === 'si' && (
              <span className="text-xs font-bold px-2 py-1 rounded bg-df-success/10 text-df-success">
                PREVENTA
              </span>
            )}
            {enOferta && (
              <span className="text-xs font-bold px-2 py-1 rounded bg-df-danger/10 text-df-danger">
                ¡OFERTA!
              </span>
            )}
          </div>

          {producto.marca && <p className="text-sm text-df-muted mb-1">{producto.marca}</p>}
          <h1 className="font-cinzel text-3xl md:text-4xl text-df-text leading-tight mb-4">
            {producto.Juego}
          </h1>

          <div className="mb-6">
            {enOferta && formatearPrecio(precioPublico) && (
              <p className="text-sm text-df-muted line-through">
                {formatearPrecio(precioPublico)}
              </p>
            )}
            {formatearPrecio(precioFinal) ? (
              <p className="text-df-gold font-bold text-3xl">{formatearPrecio(precioFinal)}</p>
            ) : (
              <p className="text-df-muted font-medium text-xl">Consultar precio</p>
            )}
          </div>

          <p className={`text-sm mb-6 ${agotado ? 'text-df-danger' : 'text-df-success'}`}>
            {agotado
              ? 'Agotado por ahora'
              : `Disponible${stock <= 5 ? ` — quedan ${stock}` : ''}`}
          </p>

          {!agotado && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-sm text-df-muted">Cantidad</span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => cambiarCantidad(-1)}
                    disabled={cantidad <= 1}
                    aria-label="Quitar una unidad"
                    className="bg-df-surface2 hover:bg-df-border disabled:opacity-40 disabled:cursor-not-allowed text-df-text w-9 h-9 rounded flex items-center justify-center transition-colors"
                  >
                    <FaMinus className="text-xs" />
                  </button>
                  <span className="px-4 py-2 rounded bg-df-accent/20 text-df-accent font-semibold min-w-[3rem] text-center">
                    {cantidad}
                  </span>
                  <button
                    type="button"
                    onClick={() => cambiarCantidad(1)}
                    disabled={cantidad >= stock}
                    aria-label="Añadir una unidad"
                    className="bg-df-accent-strong hover:bg-df-accent-deep disabled:bg-df-surface2 disabled:text-df-muted disabled:cursor-not-allowed text-white w-9 h-9 rounded flex items-center justify-center transition-colors"
                  >
                    <FaPlus className="text-xs" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <button
                  type="button"
                  onClick={() => addToCartTracked(producto, cantidad)}
                  className="btn btn-primary flex-1 gap-2"
                >
                  <FaShoppingCart className="text-sm" /> Agregar al carrito
                </button>
                <button
                  type="button"
                  onClick={() => buyNow(producto, cantidad)}
                  className="btn btn-gold flex-1"
                >
                  Comprar ahora
                </button>
              </div>

              {enCarrito > 0 && (
                <p className="text-sm text-df-muted mb-4">
                  Ya tienes {enCarrito} en el{' '}
                  <Link to="/carrito" className="text-df-accent hover:underline">
                    carrito
                  </Link>
                  .
                </p>
              )}
            </>
          )}

          {agotado && (
            <button
              type="button"
              disabled
              className="btn bg-df-surface2 text-df-muted w-full cursor-not-allowed"
            >
              AGOTADO
            </button>
          )}

          {producto.descripcion && (
            <div className="mt-8">
              <h2 className="font-cinzel text-xl text-df-text mb-3">
                Sobre el <span className="text-df-cyan">juego</span>
              </h2>
              <p className="text-df-muted leading-relaxed whitespace-pre-line">
                {producto.descripcion}
              </p>
            </div>
          )}
        </div>
      </div>

      <FichaTecnica producto={producto} className="mt-12" />

      {producto.enlace_bgg && (
        <p className="mt-6 text-sm text-df-muted">
          Más información en{' '}
          <a
            href={producto.enlace_bgg}
            target="_blank"
            rel="noopener noreferrer"
            className="text-df-accent hover:underline"
          >
            BoardGameGeek
          </a>
        </p>
      )}
    </div>
  );
}
